import { MapStyle } from "@components/world-map/world-map-types";
import {
  getStoredMapStyle,
  setStoredMapStyle,
} from "@components/world-map/world-map-utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SetDataViewModePayload, UiState } from "@store/ui/ui-slice-types";
import { LocalStorageUtils } from "@utils/local-storage-utils";

export const initialState: UiState = {
  dataViewMode: {
    projects:
      LocalStorageUtils.getJSONItem("data-view-mode")?.projects ?? "cards",
    groups: LocalStorageUtils.getJSONItem("data-view-mode")?.groups ?? "cards",
  },

  sidebar: {
    isFullSize:
      LocalStorageUtils.getBooleanItem("is-sidebar-full-size") ?? true,

    isOpenOnMobile: false,

    isVisible: false,
  },

  search: {
    debouncedSearchText: "",
    isDebouncingSearch: false,
  },

  // We first check the local storage so that we don't have to provide all the time the query param.
  isHideMigratedWorkspacesEnabled:
    LocalStorageUtils.getBooleanItem("is-hide-migrated-workspaces-enabled") ??
    false,

  numUnreadMsg: 0,

  worldMapStyle: getStoredMapStyle(),

  isContentFullWidth: false,
};

/**
 * Slice to access state of ui
 */
const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setDataViewMode(state, action: PayloadAction<SetDataViewModePayload>) {
      state.dataViewMode[action.payload.subject] = action.payload.viewMode;
      LocalStorageUtils.setJSONItem("data-view-mode", state.dataViewMode);
    },

    setIsHideMigratedWorkspacesEnabled(state, action: PayloadAction<boolean>) {
      LocalStorageUtils.setBooleanItem(
        "is-hide-migrated-workspaces-enabled",
        action.payload
      );
      state.isHideMigratedWorkspacesEnabled = action.payload;
    },

    setNumUnreadMsg(state, action: PayloadAction<number>) {
      state.numUnreadMsg = action.payload;
    },

    updateSidebar(
      state: UiState,
      action: PayloadAction<Partial<UiState["sidebar"]>>
    ) {
      state.sidebar = { ...state.sidebar, ...action.payload };

      if (action.payload.isFullSize !== undefined) {
        LocalStorageUtils.setBooleanItem(
          "is-sidebar-full-size",
          state.sidebar.isFullSize
        );
      }
    },

    setDebouncedSearchText(state: UiState, action: PayloadAction<string>) {
      state.search.debouncedSearchText = action.payload;
    },

    setIsDebouncingSearch(state: UiState, action: PayloadAction<boolean>) {
      state.search.isDebouncingSearch = action.payload;
    },

    /** Sets the map style in both the redux state and local storage */
    setWorldMapStyle(state, action: PayloadAction<MapStyle>) {
      setStoredMapStyle(action.payload);
      state.worldMapStyle = action.payload;
    },

    /** Toggles whether the page content spans the full width of the main area */
    setContentFullWidth(state, action: PayloadAction<boolean>) {
      state.isContentFullWidth = action.payload;
    },

    resetUiState: () => initialState,
  },
});

export const {
  setDataViewMode,
  setIsHideMigratedWorkspacesEnabled,
  setNumUnreadMsg,
  updateSidebar,
  setDebouncedSearchText,
  setIsDebouncingSearch,
  setWorldMapStyle,
  setContentFullWidth,
  resetUiState,
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;
